import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import type { ResultProfile } from '../data/quizData';
interface QuizResultProps {
  result: ResultProfile;
  onContinue: () => void;
}
export function QuizResult({
  result,
  onContinue
}: QuizResultProps) {
  return <motion.div className="min-h-screen w-full flex items-center justify-center px-4 py-12" initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5,
    ease: 'easeOut'
  }} data-id="element-181">
      <div className="max-w-2xl w-full" data-id="element-182">
        {/* Result Badge */}
        <motion.div className="text-center mb-8" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15,
        duration: 0.5
      }} data-id="element-183">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-cyan-400/70 mb-3" data-id="element-184">
            Tu resultado
          </span>
          <div className="text-6xl mb-4" data-id="element-185">{result.emoji}</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-2" data-id="element-186">
            {result.title}
          </h1>
          <p className="text-lg font-semibold text-transparent bg-clip-text" style={{
          backgroundImage: 'linear-gradient(135deg, #00d4ff, #0ea5e9)'
        }} data-id="element-187">
            {result.subtitle}
          </p>
        </motion.div>

        {/* Result Card */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.5
      }} className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-6 sm:p-8 mb-8" style={{
        boxShadow: '0 0 40px rgba(0, 212, 255, 0.05), inset 0 1px 0 rgba(255,255,255,0.05)'
      }} data-id="element-188">
          {/* Diagnosis */}
          <div className="mb-8" data-id="element-189">
            <h3 className="text-sm font-bold uppercase tracking-wider text-cyan-400/60 mb-3" data-id="element-190">
              Tu diagnóstico
            </h3>
            <p className="text-white/75 text-base sm:text-lg leading-relaxed" data-id="element-191">
              {result.diagnosis}
            </p>
          </div>

          {/* Route */}
          <div data-id="element-192">
            <h3 className="text-sm font-bold uppercase tracking-wider text-cyan-400/60 mb-4" data-id="element-193">
              Tu Ruta 48H
            </h3>
            <div className="flex flex-col gap-3" data-id="element-194">
              {result.steps.map((step, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              x: -16
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.45 + idx * 0.1,
              duration: 0.35
            }} className="flex items-start gap-3" data-id="element-195">
                  <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-cyan-500/15 border border-cyan-500/25 flex items-center justify-center mt-0.5" data-id="element-196">
                    <span className="text-xs font-bold text-cyan-400" data-id="element-197">
                      {idx + 1}
                    </span>
                  </div>
                  <p className="text-white/70 text-base leading-snug" data-id="element-198">{step}</p>
                </motion.div>)}
            </div>
          </div>
        </motion.div>

        {/* CTA Text */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8,
        duration: 0.4
      }} className="text-center" data-id="element-199">
          <p className="text-white/50 text-base mb-6 flex items-center justify-center gap-2" data-id="element-200">
            <CheckIcon className="w-4 h-4 text-cyan-400" data-id="element-201" />
            {result.cta}
          </p>

          <button onClick={onContinue} className="group relative inline-flex items-center gap-2 px-10 py-4 rounded-xl text-lg font-bold text-black transition-all duration-200 hover:scale-105 active:scale-100" style={{
          background: 'linear-gradient(135deg, #f59e0b, #ff6b2b)',
          boxShadow: '0 0 30px rgba(245, 158, 11, 0.3), 0 4px 20px rgba(255, 107, 43, 0.2)'
        }} aria-label="Ver mi Plan Completo de 48H" data-id="element-202">
            Ver mi Plan Completo de 48H
            <ArrowRightIcon className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-203" />
          </button>
        </motion.div>
      </div>
    </motion.div>;
}